import { useEffect, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

/**
 * A custom hook to sync the global drawer's state with a URL query parameter.
 * This allows for deep linking into specific drawers.
 *
 * @param {string} paramName - The name of the query parameter to use (e.g., 'edit_invoice')
 * @param {function} onOpenDrawer - A stable callback (wrapped in useCallback) triggered when the parameter is present in the URL.
 * @returns {function} openDrawerWithParam - A function to call instead of directly dispatching toggleDrawer. It sets the URL param.
 */
const useDrawerDeepLink = (paramName, onOpenDrawer) => {
	const [searchParams, setSearchParams] = useSearchParams()
	const showDrawer = useSelector((state) => state.getIn(['root', 'showDrawer']))
	const prevShowDrawer = useRef(showDrawer)

	// Watch for the URL parameter and trigger the open callback
	useEffect(() => {
		if (!paramName) return

		const paramValue = searchParams.get(paramName)
		if (paramValue && typeof onOpenDrawer === 'function') {
			onOpenDrawer(paramValue)
		}
	}, [searchParams.get(paramName), paramName,onOpenDrawer])

	// Watch for the drawer actively closing and clean up the URL parameter
	useEffect(() => {
		if (prevShowDrawer.current === true && showDrawer === false) {
			if (searchParams.has(paramName)) {
				const newParams = new URLSearchParams(searchParams)
				newParams.delete(paramName)
				setSearchParams(newParams, { replace: true })
			}
		}
		prevShowDrawer.current = showDrawer
	}, [showDrawer, searchParams,  paramName])

	// A helper function to trigger the drawer open via URL parameter
	const openDrawerWithParam = useCallback((paramValue) => {
		if (!paramName || !paramValue) {
			console.warn('useDrawerDeepLink: Missing paramName or paramValue when attempting to open drawer.')
			return
		}
		
		const newParams = new URLSearchParams(searchParams)
		newParams.set(paramName, paramValue)
		setSearchParams(newParams, { replace: true })
	}, [searchParams, setSearchParams, paramName])

	return openDrawerWithParam
}

export default useDrawerDeepLink
